/**
 * ElecBid — Statement Reconciliation Modal
 * Allows uploading a supplier statement (PDF or CSV), extracting line items,
 * comparing against pending_invoices, and adding missing items.
 */

import { useState, useRef } from "react";

const STATUS_ICONS = {
  matched:          { icon: "✅", label: "Matched",        color: "#22c55e" },
  pushed:           { icon: "✅", label: "In Jobber",       color: "#22c55e" },
  amount_mismatch:  { icon: "⚠️", label: "Amt Mismatch",   color: "#f59e0b" },
  missing:          { icon: "❌", label: "Missing",         color: "#ef4444" },
  credit:           { icon: "🔄", label: "Credit/Return",  color: "#a78bfa" },
};

const KNOWN_SUPPLIERS = ["EB Horsman", "City Electric", "Gescan", "Westburne", "Other"];

function detectSupplier(filename) {
  const n = (filename || "").toLowerCase();
  if (n.includes("horsman") || n.includes("ebh")) return "EB Horsman";
  if (n.includes("city"))                          return "City Electric";
  if (n.includes("gescan"))                        return "Gescan";
  if (n.includes("westburne"))                     return "Westburne";
  return "";
}

export default function StatementReconcile({ onClose, onDone, session }) {
  const [phase, setPhase]         = useState("upload");  // upload | extracting | results | saving | done
  const [supplier, setSupplier]   = useState("");
  const [file, setFile]           = useState(null);
  const [error, setError]         = useState(null);
  const [statementDate, setStatementDate] = useState(null);
  const [currency, setCurrency]   = useState("CAD");
  const [results, setResults]     = useState([]);
  const [summary, setSummary]     = useState({});
  const [addingIds, setAddingIds] = useState(new Set());
  const [addedIds, setAddedIds]   = useState(new Set());
  const [addingAll, setAddingAll] = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);
  const fileRef = useRef(null);

  function handleFileChange(f) {
    if (!f) return;
    setFile(f);
    setError(null);
    const detected = detectSupplier(f.name);
    if (detected) setSupplier(detected);
  }

  async function runExtractAndReconcile() {
    if (!file) return;
    setError(null);
    setPhase("extracting");

    try {
      // Ensure DB table + columns exist
      await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "ensureTable" }),
      });

      // Extract line items
      let extractRes;
      if (file.name.toLowerCase().endsWith(".csv")) {
        const csvText = await file.text();
        extractRes = await fetch("/api/reconcile-statement", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "extract", csvText, supplier }),
        }).then(r => r.json());
      } else {
        // PDF
        const pdfBase64 = await fileToBase64(file);
        extractRes = await fetch("/api/reconcile-statement", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "extract", pdfBase64, supplier }),
        }).then(r => r.json());
      }

      if (!extractRes.ok) throw new Error(extractRes.error || "Extraction failed");

      const extractedItems  = extractRes.items || [];
      const extractedDate   = extractRes.statement_date || null;
      const extractedSupplier = extractRes.supplier || supplier;
      const extractedCurrency = (extractRes.currency || "CAD").toUpperCase();

      if (!supplier && extractedSupplier) setSupplier(extractedSupplier);
      setStatementDate(extractedDate);
      setCurrency(extractedCurrency);

      if (!extractedItems.length) {
        setError("No line items found in the statement.");
        setPhase("upload");
        return;
      }

      // Reconcile against pending_invoices
      const reconcileRes = await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reconcile", items: extractedItems, supplier: extractedSupplier, currency: extractedCurrency }),
      }).then(r => r.json());

      if (!reconcileRes.ok) throw new Error(reconcileRes.error || "Reconciliation failed");

      setResults(reconcileRes.results || []);
      setSummary(reconcileRes.summary || {});
      setPhase("results");
    } catch (e) {
      setError(e.message);
      setPhase("upload");
    }
  }

  async function addItem(item, idx) {
    const key = `${idx}`;
    setAddingIds(prev => new Set([...prev, key]));
    try {
      const r = await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addMissing",
          items: [item],
          supplier,
          createdBy: session?.user?.email || null,
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "Failed to add");
      setAddedIds(prev => new Set([...prev, key]));
    } catch (e) {
      setSaveMsg({ type: "error", text: e.message });
    }
    setAddingIds(prev => { const s = new Set(prev); s.delete(key); return s; });
  }

  async function addAllMissing() {
    const missing = results
      .map((r, i) => ({ item: r, idx: i }))
      .filter(({ item, idx }) => item.status === "missing" && !addedIds.has(`${idx}`));
    if (!missing.length) return;

    setAddingAll(true);
    try {
      const r = await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addMissing",
          items: missing.map(({ item }) => item),
          supplier,
          createdBy: session?.user?.email || null,
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "Failed");
      const newAdded = new Set(addedIds);
      missing.forEach(({ idx }) => newAdded.add(`${idx}`));
      setAddedIds(newAdded);
      setSaveMsg({ type: "success", text: `Added ${data.inserted} missing item(s) to ElecBid` });
    } catch (e) {
      setSaveMsg({ type: "error", text: e.message });
    }
    setAddingAll(false);
  }

  async function saveReconciliation() {
    setPhase("saving");
    try {
      const r = await fetch("/api/reconcile-statement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          supplier,
          statement_date: statementDate,
          results,
          createdBy: session?.user?.email || null,
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "Save failed");
      setPhase("done");
      onDone?.();
    } catch (e) {
      setSaveMsg({ type: "error", text: e.message });
      setPhase("results");
    }
  }

  const missingCount    = results.filter((r, i) => r.status === "missing" && !addedIds.has(`${i}`)).length;
  const totalOnStatement = results.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div
        className="ps-panel"
        style={{ maxWidth: 780, width: "95%" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ps-header">
          <div>
            <div className="ps-title">Statement Reconciliation</div>
            <div className="ps-subtitle">Upload a supplier statement and compare against ElecBid</div>
          </div>
          <button className="ps-close" onClick={onClose} disabled={phase === "extracting" || phase === "saving"}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", maxHeight: "65vh", overflowY: "auto" }}>

          {/* ── Upload phase ─────────────────────────────────── */}
          {phase === "upload" && (
            <div>
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={S.label}>Supplier</div>
                  <select style={S.inp} value={supplier} onChange={e => setSupplier(e.target.value)}>
                    <option value="">— Auto-detect —</option>
                    {KNOWN_SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.csv"
                style={{ display: "none" }}
                onChange={e => handleFileChange(e.target.files?.[0])}
              />

              <div
                style={{
                  border: "2px dashed rgba(245,158,11,0.35)",
                  borderRadius: 12,
                  padding: "32px 24px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: file ? "rgba(245,158,11,0.05)" : "transparent",
                  transition: "background 0.15s",
                }}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); }}
                onDrop={e => { e.preventDefault(); handleFileChange(e.dataTransfer.files?.[0]); }}
              >
                <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
                {file ? (
                  <>
                    <div style={{ color: "#f0f0f0", fontWeight: 600, marginBottom: 4 }}>{file.name}</div>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      {(file.size / 1024).toFixed(1)} KB · {file.name.toLowerCase().endsWith(".pdf") ? "PDF" : "CSV"}
                    </div>
                    <div style={{ color: "#f59e0b", fontSize: 13, marginTop: 8 }}>Click to change file</div>
                  </>
                ) : (
                  <>
                    <div style={{ color: "#8a919c", marginBottom: 6 }}>Drag & drop or click to select</div>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>PDF statement or CSV export</div>
                  </>
                )}
              </div>

              {error && (
                <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "10px 14px", marginTop: 14, color: "#fca5a5", fontSize: 13 }}>
                  ✕ {error}
                </div>
              )}

              <button
                style={{ ...S.btn, marginTop: 20, width: "100%", background: file ? "#f59e0b" : "rgba(245,158,11,0.2)", color: file ? "#0d0f12" : "#8a919c", cursor: file ? "pointer" : "default" }}
                onClick={runExtractAndReconcile}
                disabled={!file}
              >
                Extract & Reconcile →
              </button>
            </div>
          )}

          {/* ── Extracting phase ─────────────────────────────── */}
          {phase === "extracting" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <span className="spin-lg" style={{ display: "block", margin: "0 auto 16px" }} />
              <div style={{ color: "#f0f0f0", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                Analysing statement with AI…
              </div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                Extracting line items then matching against ElecBid
              </div>
            </div>
          )}

          {/* ── Saving phase ─────────────────────────────────── */}
          {phase === "saving" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <span className="spin-lg" style={{ display: "block", margin: "0 auto 16px" }} />
              <div style={{ color: "#f0f0f0", fontSize: 14, fontWeight: 600 }}>Saving reconciliation…</div>
            </div>
          )}

          {/* ── Done phase ───────────────────────────────────── */}
          {phase === "done" && (
            <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "20px", textAlign: "center" }}>
              <div style={{ color: "#22c55e", fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                ✅ Reconciliation saved
              </div>
              <div style={{ color: "#8a919c", fontSize: 13 }}>
                {results.length} items · ${Math.abs(totalOnStatement).toFixed(2)} statement total
              </div>
            </div>
          )}

          {/* ── Results phase ────────────────────────────────── */}
          {phase === "results" && (
            <>
              {/* Summary bar */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                {[
                  { status: "matched",         label: "Matched" },
                  { status: "pushed",           label: "In Jobber" },
                  { status: "amount_mismatch",  label: "Amt Mismatch" },
                  { status: "missing",          label: "Missing" },
                  { status: "credit",           label: "Credits" },
                ].map(({ status, label }) => {
                  const cfg = STATUS_ICONS[status];
                  const count = summary[status] || 0;
                  return (
                    <div key={status} style={{
                      background: `${hexToRgba(cfg.color, 0.1)}`,
                      border: `1px solid ${hexToRgba(cfg.color, 0.25)}`,
                      borderRadius: 8,
                      padding: "6px 14px",
                      textAlign: "center",
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: cfg.color }}>{count}</div>
                      <div style={{ fontSize: 11, color: "#8a919c" }}>{label}</div>
                    </div>
                  );
                })}
                <div style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  padding: "6px 14px",
                  textAlign: "center",
                  marginLeft: "auto",
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#f0f0f0" }}>${Math.abs(totalOnStatement).toFixed(2)}</div>
                  <div style={{ fontSize: 11, color: "#8a919c" }}>Statement Total{currency && currency !== "CAD" ? ` · ${currency}` : ""}</div>
                </div>
              </div>

              {/* Bulk action */}
              {missingCount > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <button
                    style={{ ...S.btnSm, background: "rgba(239,68,68,0.15)", borderColor: "rgba(239,68,68,0.3)", color: "#fca5a5" }}
                    onClick={addAllMissing}
                    disabled={addingAll}
                  >
                    {addingAll ? "Adding…" : `➕ Add All Missing (${missingCount})`}
                  </button>
                </div>
              )}

              {saveMsg && (
                <div style={{
                  background: saveMsg.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                  border: `1px solid ${saveMsg.type === "success" ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
                  borderRadius: 8, padding: "9px 14px", marginBottom: 12,
                  color: saveMsg.type === "success" ? "#22c55e" : "#fca5a5", fontSize: 13,
                }}>
                  {saveMsg.text}
                </div>
              )}

              {/* Results table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      {["Status", "Date", "Ref #", "PO", "Type", "Amount", "Match in ElecBid", "Action"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#6b7280", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, idx) => {
                      const cfg  = STATUS_ICONS[row.status] || STATUS_ICONS.missing;
                      const key  = `${idx}`;
                      const done = addedIds.has(key);
                      const busy = addingIds.has(key);
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                          <td style={{ padding: "7px 8px", whiteSpace: "nowrap" }}>
                            <span title={cfg.label} style={{ color: cfg.color, fontSize: 14 }}>{cfg.icon}</span>
                            <span style={{ color: cfg.color, fontSize: 11, marginLeft: 5 }}>{cfg.label}</span>
                          </td>
                          <td style={{ padding: "7px 8px", color: "#8a919c", whiteSpace: "nowrap" }}>{row.date || "—"}</td>
                          <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#c0c8d4", whiteSpace: "nowrap" }}>{row.ref_no || "—"}</td>
                          <td style={{ padding: "7px 8px", color: "#8a919c", whiteSpace: "nowrap" }}>{row.po_number || "—"}</td>
                          <td style={{ padding: "7px 8px", color: "#8a919c", fontSize: 12, whiteSpace: "nowrap" }}>{row.transaction_type || "—"}</td>
                          <td style={{ padding: "7px 8px", fontFamily: "monospace", color: row.amount < 0 ? "#a78bfa" : "#f0f0f0", whiteSpace: "nowrap", textAlign: "right" }}>
                            {row.amount < 0 ? `−$${Math.abs(row.amount).toFixed(2)}` : `$${(row.amount || 0).toFixed(2)}`}
                          </td>
                          <td style={{ padding: "7px 8px", maxWidth: 180 }}>
                            {row.match ? (
                              <div style={{ fontSize: 12 }}>
                                <div style={{ color: "#c0c8d4" }}>Inv #{row.match.invoice_number}</div>
                                {row.match.jobber_job_number && (
                                  <div style={{ color: "#22c55e", fontSize: 11 }}>Job #{row.match.jobber_job_number}</div>
                                )}
                                {row.status === "amount_mismatch" && (() => {
                                  const useNative = currency !== "CAD" && row.match.original_amount != null && (row.match.currency || "CAD") === currency;
                                  const amt = useNative ? row.match.original_amount : row.match.total;
                                  return <div style={{ color: "#f59e0b", fontSize: 11 }}>ElecBid: ${parseFloat(amt || 0).toFixed(2)}{useNative ? ` ${currency}` : ""}</div>;
                                })()}
                              </div>
                            ) : <span style={{ color: "#4a5260" }}>—</span>}
                          </td>
                          <td style={{ padding: "7px 8px", whiteSpace: "nowrap" }}>
                            {row.status === "missing" && (
                              done ? (
                                <span style={{ color: "#22c55e", fontSize: 12 }}>✓ Added</span>
                              ) : (
                                <button
                                  style={{ ...S.btnSm, background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.3)", color: "#fca5a5" }}
                                  onClick={() => addItem(row, idx)}
                                  disabled={busy}
                                >
                                  {busy ? "…" : "➕ Add"}
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {(phase === "results" || phase === "done") && (
          <div className="ps-footer">
            {phase === "results" && (
              <>
                <button
                  style={{ ...S.btnSm, background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.1)", color: "#8a919c" }}
                  onClick={() => { setPhase("upload"); setFile(null); setResults([]); setSaveMsg(null); }}
                >
                  ← New Upload
                </button>
                <button
                  style={{ ...S.btnSm, background: "rgba(165,206,58,0.15)", borderColor: "rgba(165,206,58,0.3)", color: "#a5ce3a" }}
                  onClick={saveReconciliation}
                >
                  💾 Save Reconciliation
                </button>
              </>
            )}
            {phase === "done" && (
              <button style={{ ...S.btnSm, background: "rgba(34,197,94,0.12)", borderColor: "rgba(34,197,94,0.3)", color: "#22c55e" }} onClick={onClose}>
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target.result;
      // Strip data: prefix if present
      const b64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function hexToRgba(hex, alpha) {
  const colorMap = {
    "#22c55e": `rgba(34,197,94,${alpha})`,
    "#f59e0b": `rgba(245,158,11,${alpha})`,
    "#ef4444": `rgba(239,68,68,${alpha})`,
    "#a78bfa": `rgba(167,139,250,${alpha})`,
  };
  return colorMap[hex] || `rgba(255,255,255,${alpha})`;
}

// ── Shared inline styles ──────────────────────────────────────────────────────

const S = {
  label: {
    fontSize: 11,
    color: "#6b7280",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    display: "block",
  },
  inp: {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "8px 12px",
    color: "#f0f0f0",
    fontSize: 14,
    boxSizing: "border-box",
  },
  btn: {
    border: "none",
    borderRadius: 9,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    transition: "opacity 0.15s",
  },
  btnSm: {
    border: "1px solid",
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
  },
};
